import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ReportFilterPreset } from "../services/report-settings.api";
import styles from "./ReportPresetBar.module.scss";

interface ReportPresetBarProps {
  presets: ReportFilterPreset[];
  /** Ekranda o an kurulu filtre — "kaydet" bu değeri saklar. */
  currentFilters: Record<string, unknown>;
  onApply: (filters: Record<string, unknown>) => void;
  onSave: (name: string, filters: Record<string, unknown>, isDefault: boolean) => Promise<void>;
  onDelete: (presetID: string) => Promise<void>;
}

/**
 * Kayıtlı filtre setleri çubuğu (R11).
 *
 * Filtreler her ziyarette sıfırlanıyor ve sık kullanılan kombinasyonu kurmak
 * her seferinde elle yapılıyordu. Burada kullanıcı mevcut filtreyi isimlendirip
 * kaydeder, tek tıkla geri yükler ve istediğini varsayılan yapar.
 */
const ReportPresetBar: React.FC<ReportPresetBarProps> = ({
  presets,
  currentFilters,
  onApply,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [isNaming, setIsNaming] = useState(false);
  const [name, setName] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsBusy(true);
    try {
      await onSave(trimmed, currentFilters, makeDefault);
      toast.success(t("reportSettings.presetSaved"));
      setName("");
      setMakeDefault(false);
      setIsNaming(false);
    } catch {
      /* hata toast'ı interceptor tarafından gösterilir */
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (preset: ReportFilterPreset) => {
    setIsBusy(true);
    try {
      await onDelete(preset.id);
      toast.success(t("reportSettings.presetDeleted"));
    } catch {
      /* hata toast'ı interceptor tarafından gösterilir */
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className={styles.bar}>
      <span className={styles.label}>{t("reportSettings.presetsLabel")}</span>

      {presets.length === 0 && !isNaming && (
        <span className={styles.empty}>{t("reportSettings.presetsEmpty")}</span>
      )}

      <div className={styles.chips}>
        {presets.map((preset) => (
          <span key={preset.id} className={styles.chip}>
            <button
              type="button"
              className={styles.chipApply}
              onClick={() => onApply(preset.filters)}
              title={t("reportSettings.presetApply")}
            >
              {preset.name}
              {preset.isDefault && <span className={styles.defaultBadge}>★</span>}
            </button>
            <button
              type="button"
              className={styles.chipDelete}
              onClick={() => handleDelete(preset)}
              disabled={isBusy}
              aria-label={t("reportSettings.presetDelete")}
              title={t("reportSettings.presetDelete")}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      {isNaming ? (
        <div className={styles.form}>
          <input
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("reportSettings.presetNamePlaceholder")}
            maxLength={80}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSave();
              if (e.key === "Escape") setIsNaming(false);
            }}
          />
          <label className={styles.defaultToggle}>
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
            />
            {t("reportSettings.presetMakeDefault")}
          </label>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSave}
            disabled={!name.trim() || isBusy}
          >
            {t("buttons.save")}
          </button>
          <button type="button" className={styles.ghostButton} onClick={() => setIsNaming(false)}>
            {t("buttons.cancel")}
          </button>
        </div>
      ) : (
        <button type="button" className={styles.ghostButton} onClick={() => setIsNaming(true)}>
          + {t("reportSettings.presetSaveCurrent")}
        </button>
      )}
    </div>
  );
};

export default ReportPresetBar;
